/* eslint-disable no-unused-vars */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, notification, DatePicker } from 'antd'
import { Icon } from '@iconify/react'
import { CalendarOutlined, LoadingOutlined, DownloadOutlined } from '@ant-design/icons'
import { Applications } from '../../../../services'
import moment from 'moment'

const bodyStyle = {
  padding: 0
}

const ExportApplicationsModal = ({ applicationStatus, visible, onCancel }) => {
  const [loading, setLoading] = useState(false)
  const [csvStartDate, setCsvStartDate] = useState(null)
  const [csvEndDate, setCsvEndDate] = useState(null)

  const handleCSVDateFilter = (_, date) => {
    const startDate = date[0]
    const endDate = date[1]

    setCsvStartDate(startDate)
    setCsvEndDate(endDate)
  }

  const handleSubmission = async () => {
    if (!csvStartDate || !csvEndDate) {
      notification.error({
        message: 'Input validation error',
        description: 'Start date and End date are required for export'
      })
      return
    }

    setLoading(true)

    // Format dates as YYYY-MM-DD
    const formattedStartDate = moment(csvStartDate).format('YYYY-MM-DD')
    const formattedEndDate = moment(csvEndDate).format('YYYY-MM-DD')

    await Applications.exportApplications(formattedStartDate, formattedEndDate, applicationStatus)
      .then(response => {
        notification.success({
          message: 'Success',
          description: 'Applications exported successfully'
        })

        // Determine file extension based on response type (API returns Excel file)
        const contentType =
          response.headers['content-type'] ||
          response.headers['Content-Type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        const fileExtension =
          contentType.includes('csv') || contentType.includes('text/csv') ? '.csv' : '.xlsx'
        const blob = new Blob([response.data], { type: contentType })

        // Create a URL for the Blob and download it
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        const fileName = `applications_${
          applicationStatus || 'all'
        }_${formattedStartDate}_${formattedEndDate}${fileExtension}`
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove() // Clean up after the download
        window.URL.revokeObjectURL(downloadUrl) // Clean up the URL object

        onCancel()
      })
      .catch(error => {
        notification.error({
          message: 'Something went wrong',
          description:
            error?.response?.data?.message || error?.message || 'Failed to export applications'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      className='info-modal'
      closeIcon={
        <Icon
          icon='iconamoon:close-thin'
          color='#b1b1ff'
          className='mt-4 mr-8 text-white text-4xl rounded-full bg-white'
        />
      }
      bodyStyle={bodyStyle}
      width={800}
    >
      <div className='modal-header bg-primary px-6 flex justify-between pb-4'>
        <h2 className='font-bold tracking-tight mt-4 text-white text-2xl'>Export Applications</h2>
      </div>
      <div className='px-4 pb-8'>
        <p className='font-bold text-lg py-8 pl-8'>
          Kindly select the start and end date for your query
        </p>
        {applicationStatus && (
          <p className='text-sm text-gray-600 pl-8 pb-4'>
            Status: <span className='font-semibold'>{applicationStatus}</span>
          </p>
        )}
        <div className='flex flex-col gap-8 justify-center px-8'>
          <div className='flex items-end gap-4'>
            <div>
              <div className='mb-2'>Date Range</div>
              <div className='relative flex border rounded-md'>
                <DatePicker.RangePicker
                  onChange={handleCSVDateFilter}
                  picker='date'
                  suffixIcon={<CalendarOutlined />}
                  format='YYYY-MM-DD'
                />
              </div>
            </div>
          </div>

          <div className='flex justify-start'>
            <button
              onClick={handleSubmission}
              disabled={loading}
              className='bg-primary hover:bg-primary-200 text-white font-medium rounded-lg px-6 py-2.5 flex items-center gap-2.5 transition-all duration-200 ease-in-out hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100'
            >
              {loading ? (
                <>
                  <LoadingOutlined spin className='text-base' />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <DownloadOutlined className='text-base' />
                  <span>Export</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

ExportApplicationsModal.propTypes = {
  applicationStatus: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ExportApplicationsModal
